import dayjs from 'dayjs'

const TIMELINE_START_HOUR = 6
const TIMELINE_MINUTES = 24 * 60
const MIN_SHIFT_HEIGHT_MINUTES = 60
const PRAGUE_TIMEZONE = 'Europe/Prague'

const isCrossMidnightShift = (
  startDateTime: dayjs.Dayjs,
  endDateTime: dayjs.Dayjs,
  originalInterval?: { starting_from: string; ending_at: string }
): boolean => {
  if (originalInterval) {
    const startUTC = dayjs.utc(originalInterval.starting_from)
    const endUTC = dayjs.utc(originalInterval.ending_at)
    return startUTC.format('YYYY-MM-DD') !== endUTC.format('YYYY-MM-DD')
  }
  return startDateTime.format('YYYY-MM-DD') !== endDateTime.format('YYYY-MM-DD')
}

const getTimelineStart = (
  isCrossMidnight: boolean,
  timelineStart: dayjs.Dayjs,
  shiftStartPrague: dayjs.Dayjs
): dayjs.Dayjs => {
  if (isCrossMidnight) {
    return timelineStart.tz(PRAGUE_TIMEZONE).hour(TIMELINE_START_HOUR).minute(0).second(0).millisecond(0)
  }
  const shiftDate = shiftStartPrague.format('YYYY-MM-DD')
  return dayjs.tz(`${shiftDate} 06:00:00`, PRAGUE_TIMEZONE)
}

const calculateShiftMinutes = (
  startPrague: dayjs.Dayjs,
  endPrague: dayjs.Dayjs,
  timelineStartPrague: dayjs.Dayjs,
  isCrossMidnight: boolean
): { startMinutes: number; endMinutes: number } => {
  let startMinutes = startPrague.diff(timelineStartPrague, 'minutes')
  let endMinutes = endPrague.diff(timelineStartPrague, 'minutes')

  if (isCrossMidnight && endMinutes < startMinutes) {
    endMinutes += TIMELINE_MINUTES
  }

  return { startMinutes, endMinutes }
}

const clampToTimelineBounds = (minutes: number): number => {
  return Math.max(0, Math.min(TIMELINE_MINUTES, minutes))
}

const ensureMinimumHeight = (posStart: number, posEnd: number): number => {
  if (posEnd - posStart < MIN_SHIFT_HEIGHT_MINUTES) {
    const newEnd = posStart + MIN_SHIFT_HEIGHT_MINUTES
    return Math.min(TIMELINE_MINUTES, newEnd)
  }
  return posEnd
}

export const getShiftPosition =
  (timelineStart: dayjs.Dayjs) =>
  (
    _id: string,
    startDateTime: dayjs.Dayjs,
    endDateTime: dayjs.Dayjs,
    originalInterval?: { starting_from: string; ending_at: string }
  ) => {
    const startPrague = startDateTime.tz(PRAGUE_TIMEZONE)
    const endPrague = endDateTime.tz(PRAGUE_TIMEZONE)

    const isCrossMidnight = isCrossMidnightShift(startDateTime, endDateTime, originalInterval)
    const timelineStartPrague = getTimelineStart(isCrossMidnight, timelineStart, startPrague)

    const { startMinutes, endMinutes } = calculateShiftMinutes(
      startPrague,
      endPrague,
      timelineStartPrague,
      isCrossMidnight
    )

    const posStart = clampToTimelineBounds(startMinutes)
    const posEnd = clampToTimelineBounds(endMinutes)

    // No overlap with timeline
    if (posEnd <= posStart) {
      return { top: '0%', height: '0%' }
    }

    const finalPosEnd = ensureMinimumHeight(posStart, posEnd)

    return {
      top: `${(posStart / TIMELINE_MINUTES) * 100}%`,
      height: `${((finalPosEnd - posStart) / TIMELINE_MINUTES) * 100}%`,
    }
  }

// Generate time slots for 6:00-6:00 format
export const generateTimeSlots = (): string[] => {
  return Array.from({ length: 25 }, (_, index) => {
    const hour = TIMELINE_START_HOUR + index
    const displayHour = hour >= 24 ? hour - 24 : hour
    return `${displayHour.toString().padStart(2, '0')}:00`
  })
}
